import { canBeBillable, defaultBillable, rateSnapshotFor } from "./billing";
import { makeId } from "./clock";
import type { Task, TimeEntry } from "./entities";
import { isLive, type Dataset, type OperationFactory, type SyncOperation } from "./operations";
import { setTaskDone } from "./tasks";

/** Live entries with no end, newest start first. Normally zero or one. */
export function runningEntries(dataset: Dataset): TimeEntry[] {
  return Object.values(dataset.timeEntries)
    .filter((entry) => isLive(entry) && entry.end === null)
    .sort((a, b) => b.start - a.start || (a.id < b.id ? 1 : -1));
}

export function runningEntry(dataset: Dataset): TimeEntry | undefined {
  return runningEntries(dataset)[0];
}

export interface NewEntryInput {
  projectId: string | null;
  taskId?: string | null;
  description?: string;
  billable?: boolean;
  /** The planned Event occurrence this time comes from. */
  eventId?: string | null;
  eventOccurrence?: number | null;
}

function newEntryPatch(dataset: Dataset, input: NewEntryInput, start: number, end: number | null): Record<string, unknown> {
  const project = input.projectId ? dataset.projects[input.projectId] : undefined;
  const billable = canBeBillable(project) && (input.billable ?? defaultBillable(project));
  return {
    projectId: input.projectId,
    taskId: input.taskId ?? null,
    description: input.description ?? "",
    start,
    end,
    billable,
    rateSnapshot: rateSnapshotFor(dataset, input.projectId, billable),
    invoiceId: null,
    eventId: input.eventId ?? null,
    eventOccurrence: input.eventOccurrence ?? null
  };
}

/** Starts a Timer. A running Timer is stopped at the same instant, so only one ever runs. */
export function startTimer(dataset: Dataset, input: NewEntryInput, factory: OperationFactory, now: number): SyncOperation[] {
  const operations = stopTimer(dataset, factory, now);
  operations.push(factory.op("timeEntry", makeId("entry"), newEntryPatch(dataset, input, now, null)));
  return operations;
}

export function stopTimer(dataset: Dataset, factory: OperationFactory, now: number): SyncOperation[] {
  return runningEntries(dataset).map((entry) => factory.op("timeEntry", entry.id, { end: Math.max(entry.start, now) }));
}

/** The running Timer's Task while it is still open to complete. */
export function runningTask(dataset: Dataset): Task | undefined {
  const taskId = runningEntry(dataset)?.taskId;
  const task = taskId ? dataset.tasks[taskId] : undefined;
  return task && isLive(task) && task.doneAt === null ? task : undefined;
}

/** Done: stops the Timer and completes its Task, with the Task's open sub-tasks, at the same instant. */
export function completeRunningTask(dataset: Dataset, factory: OperationFactory, now: number): SyncOperation[] {
  const task = runningTask(dataset);
  if (!task) return [];
  return [...stopTimer(dataset, factory, now), ...setTaskDone(dataset, factory, task.id, true, now)];
}

/** Records a finished entry. */
export function logEntry(dataset: Dataset, input: NewEntryInput, start: number, end: number, factory: OperationFactory): SyncOperation[] {
  return [factory.op("timeEntry", makeId("entry"), newEntryPatch(dataset, input, start, end))];
}

/**
 * Cross-device conflict rule: when several Timers run (started on different devices), the latest
 * start wins and every older one is stopped at that start. Every device computes the same result.
 */
export function resolveRunningConflicts(dataset: Dataset, factory: OperationFactory): SyncOperation[] {
  const [winner, ...older] = runningEntries(dataset);
  if (!winner) return [];
  return older.map((entry) => factory.op("timeEntry", entry.id, { end: Math.max(entry.start, winner.start) }));
}

/** Live entries overlapping [start, end). The running Timer counts until `now`. */
export function overlappingEntries(dataset: Dataset, start: number, end: number, now: number): TimeEntry[] {
  return Object.values(dataset.timeEntries).filter((entry) => isLive(entry) && entry.start < end && (entry.end ?? now) > start);
}
